export default function HollywoodDestinationsSection() {
  const stats = [
    {
      value: "62%",
      label: "of Hollywood requests are Caribbean cruise or resort departures with under 3 weeks notice",
    },
    {
      value: "1–2 weeks",
      label: "average notice for Latin American family and business trips from Hollywood",
    },
    {
      value: "Jan–Apr",
      label: "peak travel season — passport renewals spike before spring break and dry-season family visits",
    },
    {
      value: "24-hour",
      label: "shortest emergency departure window we've accommodated for a Hollywood traveler",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common destination for Hollywood travelers — whether by cruise from nearby Port Everglades or by direct flight from FLL to Nassau, San Juan, or Montego Bay. The proximity of both the airport and cruise port makes Caribbean travel an impulse decision for many Hollywood residents, which often leads to last-minute passport discoveries. All cruise passengers and most air travelers need valid passports with at least six months remaining validity.",
      urgency: "Cruise embarkation deadlines are absolute — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, and Cozumel are staple destinations for Hollywood families seeking beach vacations within a short flight from FLL. Direct and connecting service to Mexico City and Guadalajara also serves the business community. Mexico requires six months of passport validity for entry, and the spring break booking surge from January through March creates compressed timelines that standard processing cannot accommodate.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Colombia & South America",
      context: "Bogota, Medellin, Cali, and Cartagena draw Hollywood&apos;s substantial Colombian community for family visits, business meetings, and cultural events. Sao Paulo, Rio de Janeiro, and Lima serve the Brazilian and Peruvian communities. FLL has expanded South American direct service, making these destinations increasingly accessible without connecting through Miami. Each country requires six months of passport validity.",
      urgency: "Family emergencies in Colombia or Brazil can require travel within 48–72 hours — standard processing is not an option",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Cuba & Dominican Republic",
      context: "Heritage travel and family visits to Cuba and the Dominican Republic are central to many Hollywood households. Whether visiting relatives in Havana, Santiago, Santo Domingo, or Puerto Plata, these trips carry emotional weight and urgency that commercial travel cannot replicate. The Cuban and Dominican communities in Hollywood create some of the most time-sensitive passport situations we handle.",
      urgency: "Family visit travel is often booked with 1–2 weeks of notice — expired passports require immediate expedited processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto and Montreal draw Hollywood business travelers for finance, healthcare, and tech conferences. Canadian ski destinations like Whistler and Banff attract winter sports enthusiasts from the younger Hollywood demographic. Canada requires a valid passport for all air arrivals, and many Hollywood families travel with minors whose five-year passports expire faster than expected.",
      urgency: "Business conference registration deadlines often require confirmed travel 4–6 weeks in advance",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe",
      context: "Paris, London, Rome, and Barcelona attract Hollywood arts professionals from the Downtown Arts District, as well as affluent families planning summer cultural tours. The Schengen Area requires three months of passport validity beyond your planned departure date — a rule that catches many travelers who assume their passport is fine because it has not technically expired yet.",
      urgency: "Summer Europe bookings happen in March–April — passport validity checks should happen then, not in June",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Hollywood Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Hollywood
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Hollywood travelers head to the Caribbean, Latin America, Mexico, Europe, and Canada for a mix of family visits, cruises, beach vacations, and business travel. These are the destinations that create the most passport urgency for our Hollywood clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Fort Lauderdale", href: "/cities/fort-lauderdale/" },
              { name: "Miami", href: "/cities/miami/" },
              { name: "Boca Raton", href: "/cities/boca-raton/" },
              { name: "Aventura", href: "/cities/aventura/" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach/" },
              { name: "Plantation", href: "/cities/plantation/" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}